import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import Error from '@mui/icons-material/Error';
import Search from '@mui/icons-material/Search';
import StopCircleOutlined from '@mui/icons-material/StopCircleOutlined';
import { DateRange, SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useState, useEffect, useRef, useMemo } from 'react';
import { UseFormWatch } from 'react-hook-form';
import { ResourceCalendar, RuleModel } from '../../../../types';
import { useMutation } from '@tanstack/react-query';
import { RulesEngineFormValues } from './rules-engine-form-page';
import {
  previewImpact,
  PreviewImpactRequest,
} from '../../../api/rules-engine-api';
import { triggerEventOptions } from './trigger-event-options';
import { actionOptions } from './action-options';
import RulesSummaryIfSection from './rules-summary-if-section';
import RuleSummaryThenSection from './rules-summary-then-section';
import RulesSummaryWhenSection from './rules-summary-when-section';
import SharedButton from '../../../shared/buttons/shared-button';
import {
  generateRuleSummary,
  getConditionSummaryItems,
} from './rule-summary-panel-utils';

interface RuleSummaryPanelProps {
  watch: UseFormWatch<RulesEngineFormValues>;
  getModel: () => RuleModel;
  onPreviewImpact?: (startDate: string, endDate: string) => void;
  disablePreview?: boolean;
  resourceCalendars?: ResourceCalendar[];
}

export default function RuleSummaryPanel({
  watch,
  getModel,
  onPreviewImpact,
  disablePreview = false,
  resourceCalendars,
}: RuleSummaryPanelProps) {
  const theme = useTheme();

  const defaultEnd = dayjs();
  const defaultStart = defaultEnd.subtract(30, 'day');
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([
    defaultStart,
    defaultEnd,
  ]);
  const dateRangeExceeds60Days =
    dateRange[0] != null &&
    dateRange[1] != null &&
    dateRange[1].diff(dateRange[0], 'day') > 60;
  const [matchCount, setMatchCount] = useState<number | undefined>(undefined);
  const [showOutdatedWarning, setShowOutdatedWarning] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const cancelledRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const colorWarning = showOutdatedWarning
    ? theme.palette.text.disabled
    : theme.palette.text.primary;

  const { mutate: runPreview, isPending: isLoading } = useMutation({
    mutationFn: ({
      payload,
      signal,
    }: {
      payload: PreviewImpactRequest;
      signal: AbortSignal;
    }) => previewImpact(payload, signal),
    onSuccess: (result) => {
      if (cancelledRef.current) return;
      setMatchCount(result.matchCount);
    },
    onError: () => {
      if (cancelledRef.current) return;
      setTimedOut(true);
    },
  });

  // Trigger
  const triggerInfo = watch('triggerInfo');
  const triggerSources = watch('triggerSources');

  const allSource = watch('allSource');
  const {
    triggerGroup,
    triggerEvent,
    occursEverytime,
    triggerThreshold,
    hasTimeDuration,
    triggerDuration,
    triggerDurationType,
    triggerCalendarSources,
    fileMimeTypes,
  } = triggerInfo || {};
  const triggerCalendarSourceNames = useMemo(() => {
    return (
      triggerCalendarSources?.map(
        (resource) =>
          resourceCalendars?.find(
            (calendar) =>
              calendar.resourceEmail === resource ||
              calendar.resourceId === resource
          )?.resourceName || resource
      ) ?? []
    );
  }, [resourceCalendars, triggerCalendarSources]);

  // Drive filters
  const hasFileTypeFilter = watch('hasFileTypeFilter');
  const hasFileSizeFilter = watch('hasFileSizeFilter');
  const hasSharingPermissionsFilter = watch('hasSharingPermissionsFilter');
  const selectedSharingOption = watch('selectedSharingOption');

  // Actions
  const actionTodo = watch('actionInfo.actionTodo');

  // Admin filter
  const hasProfileFieldFilter = watch('hasProfileFieldFilter');
  const profileFieldFilter = watch('profileFieldFilter');

  const {
    profileInfoType,
    profileFieldType,
    profileFieldValues,
    profileFieldRegex,
    valueInputMode,
    profileFieldValueExact,
    fieldDataType,
  } = profileFieldFilter || {};

  // Group filter
  const groupFilter = watch('groupFilter');
  const {
    hasGroupNameFilter,
    groupNameMode,
    groupName,
    groupNameExact,
    groupNameRegex,
    hasMemberCountFilter,
    memberCountMode,
    memberCountOperator,
    memberCount,
    memberCountRangeLessThan,
    memberCountRangeGreaterThan,
  } = groupFilter || {};

  // Condition blocks
  const conditionBlocks = watch('conditionBlocks');
  const conditionBlocksStr = JSON.stringify(conditionBlocks);

  // --- Derived display values ---
  const triggerEventOption = triggerEventOptions.find(
    (opt) => opt.value === triggerEvent && opt.type === triggerGroup
  );
  const triggerActionLabel = triggerEventOption?.displayValue ?? '-';

  const hasAnySources =
    (triggerSources && triggerSources.length > 0) || allSource;

  const sourceItems = useMemo<string[]>(() => {
    return [
      ...(triggerSources
        ?.map((source) => source.value || source.selectedSharedDriveId || '')
        .filter(Boolean) ?? []),
      ...triggerCalendarSourceNames,
    ];
  }, [triggerSources, triggerCalendarSourceNames]);

  const actionItems: string[] =
    actionTodo && actionTodo.length > 0
      ? actionTodo.map(
          (action) =>
            actionOptions.find((option) => option.value === action)
              ?.displayValue ?? action
        )
      : [];

  const hasCalendarSources =
    allSource || (triggerCalendarSources && triggerCalendarSources.length > 0);

  const canPreview =
    !!triggerGroup &&
    !!triggerEvent &&
    (triggerGroup === 'calendar' ? hasCalendarSources : hasAnySources);

  const disablePreviewImpactButton =
    disablePreview ||
    !canPreview ||
    !dateRange[0] ||
    !dateRange[1] ||
    dateRangeExceeds60Days;

  const PREVIEW_MATCH_LIMITS = {
    admin: 50,
    drive: 100,
    calendar: 50,
  };

  const matchLimit =
    PREVIEW_MATCH_LIMITS[
      (triggerGroup?.toLowerCase() ?? '') as keyof typeof PREVIEW_MATCH_LIMITS
    ] ?? 50;

  // Show warning when any trigger/filter/source configuration changes after preview.
  // Excludes actionTodo (actions step) and review step — those don't affect impact.
  useEffect(() => {
    setShowOutdatedWarning((prev) => (matchCount !== undefined ? true : prev));
    setTimedOut(false);
    // intentionally omit matchCount — only config changes should fire this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRange,
    triggerGroup,
    triggerEvent,
    occursEverytime,
    triggerThreshold,
    hasTimeDuration,
    triggerDuration,
    triggerDurationType,
    triggerSources,
    allSource,
    triggerCalendarSources,
    fileMimeTypes,
    hasFileTypeFilter,
    hasFileSizeFilter,
    hasSharingPermissionsFilter,
    selectedSharingOption,
    hasProfileFieldFilter,
    profileInfoType,
    profileFieldType,
    profileFieldValues,
    profileFieldRegex,
    valueInputMode,
    profileFieldValueExact,
    fieldDataType,
    hasGroupNameFilter,
    groupNameMode,
    groupName,
    groupNameExact,
    groupNameRegex,
    hasMemberCountFilter,
    memberCountMode,
    memberCountOperator,
    memberCount,
    memberCountRangeLessThan,
    memberCountRangeGreaterThan,
    conditionBlocksStr,
  ]);

  const handlePreview = () => {
    if (
      !canPreview ||
      !dateRange[0] ||
      !dateRange[1] ||
      dateRangeExceeds60Days
    ) {
      return;
    }
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    cancelledRef.current = false;
    setTimedOut(false);
    setShowOutdatedWarning(false);
    onPreviewImpact?.(dateRange[0].toISOString(), dateRange[1].toISOString());
    runPreview({
      payload: {
        model: getModel(),
        startDate: dateRange[0].valueOf(),
        endDate: dateRange[1].valueOf(),
      },
      signal: abortControllerRef.current.signal,
    });
  };

  const handleCancel = () => {
    cancelledRef.current = true;
    abortControllerRef.current?.abort();
  };

  const conditionSummaryItems = getConditionSummaryItems(conditionBlocks);

  const ruleSummary = generateRuleSummary({
    triggerSources: sourceItems,
    allSource,
    triggerGroup,
    triggerEvent: triggerActionLabel,
    occursEverytime,
    conditions: conditionSummaryItems,
    actions: actionItems,
  });

  return (
    <Card
      elevation={1}
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 1,
        px: 2,
      }}
    >
      {/* Scrollable top: Rule Summary */}
      <Box
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: '0 1 auto',
          minHeight: 0,
          pt: 1,
          px: 0.5,
          pb: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
          Rule Summary
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontStyle: 'italic',
            lineHeight: 0,
            letterSpacing: '0.15px',
          }}
        >
          Fill in the sections on the left to see your rule take shape here.
        </Typography>
        <Box
          sx={{
            mt: 1.5,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 1,
            bgcolor: theme.palette.semantic.surface.info,
            color: theme.palette.colors.neutral[600],
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          <Typography variant="caption">{ruleSummary}</Typography>
        </Box>
        <RulesSummaryWhenSection
          triggerInfo={triggerInfo}
          triggerSources={sourceItems}
          allSource={allSource}
          variant="compact"
        />
        <RulesSummaryIfSection
          conditionBlocks={conditionBlocks}
          variant="compact"
        />
        <RuleSummaryThenSection actionTodo={actionTodo} variant="compact" />
      </Box>

      {/* Fixed bottom: Estimated Impact */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pt: 2,
          pr: 1,
          pb: 1.5,
          pl: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="column" spacing={1.5}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            justifyContent="space-between"
          >
            <Typography variant="body2" fontWeight={700}>
              Estimated Impact
            </Typography>
            <Tooltip
              title={
                matchCount === undefined
                  ? 'Complete required fields to run estimate.'
                  : 'This estimate is based on historical data from the selected date range. Actual results may vary based on future user activity.'
              }
              arrow
            >
              <InfoOutlined
                sx={{
                  fontSize: 18,
                  color: theme.palette.semantic.icon.blue,
                }}
              />
            </Tooltip>
          </Stack>

          {showOutdatedWarning && (
            <Alert
              severity="warning"
              icon={<Error sx={{ color: '#E0982D' }} />}
              sx={{
                py: 0.5,
                '& .MuiAlert-message': {
                  fontSize: '0.875rem',
                },
              }}
            >
              Estimation out of date. Update to see new impact.
            </Alert>
          )}

          <DateRangePicker
            value={dateRange}
            onChange={(newValue) => setDateRange(newValue)}
            slots={{
              field: SingleInputDateRangeField,
            }}
            slotProps={{
              textField: {
                label: 'Date Range',
                fullWidth: true,
                error: dateRangeExceeds60Days,
                helperText: dateRangeExceeds60Days
                  ? 'Date range cannot exceed 60 days'
                  : '',
              },
            }}
            maxDate={dayjs()}
            minDate={dayjs().subtract(60, 'day')}
          />

          {isLoading ? (
            <SharedButton
              variant="secondary"
              fullWidth
              onClick={handleCancel}
              startIcon={<StopCircleOutlined />}
            >
              Cancel
            </SharedButton>
          ) : (
            <SharedButton
              variant="primary"
              fullWidth
              disabled={disablePreviewImpactButton}
              onClick={handlePreview}
              startIcon={<InsightsOutlined />}
            >
              Preview Impact
            </SharedButton>
          )}
        </Stack>
        <Stack alignItems="center" spacing={1.5} sx={{ flex: 1, pt: 2 }}>
          {isLoading ? (
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Skeleton variant="rounded" width="100%" height={48} />
              <Skeleton variant="rounded" width="100%" height={48} />
            </Stack>
          ) : timedOut ? (
            <>
              <InfoOutlined
                sx={{
                  fontSize: 24,
                  color: theme.palette.grey[500],
                }}
              />
              <Typography variant="subtitle2" fontWeight={400}>
                Simulation Timeout
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  textAlign: 'center',
                  px: 1,
                }}
              >
                We couldn't fetch the simulated impact in the allotted time.
                Your rule logic is still valid.
              </Typography>
            </>
          ) : matchCount !== undefined ? (
            matchCount === 0 ? (
              <>
                <Search
                  sx={{
                    fontSize: 24,
                    color: theme.palette.grey[500],
                  }}
                />
                <Typography variant="subtitle2" fontWeight={400}>
                  No activity Found
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: 'center',
                    px: 1,
                  }}
                >
                  There were no events matching your current filters within the
                  selected date range.
                </Typography>
              </>
            ) : (
              <>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography
                    fontWeight={700}
                    fontSize={20}
                    sx={{
                      color: colorWarning,
                    }}
                  >
                    {matchCount > matchLimit ? matchLimit : matchCount}
                  </Typography>
                  {matchCount > matchLimit && (
                    <Typography
                      fontWeight={700}
                      fontSize={20}
                      sx={{
                        color: colorWarning,
                      }}
                    >
                      +
                    </Typography>
                  )}
                </Stack>
                <Typography
                  variant="body2"
                  sx={{
                    color: colorWarning,
                  }}
                >
                  Total Matches
                </Typography>
                {matchCount > matchLimit && (
                  <Alert
                    severity="info"
                    sx={{
                      py: 0.5,
                      px: 0,
                      textAlign: 'left',
                      '& .MuiAlert-message': {
                        fontSize: '0.8rem',
                      },
                    }}
                  >
                    {`This rule matches more than ${matchLimit} historical records. The actual impact may be much higher. We recommend refining your filters to ensure accuracy.`}
                  </Alert>
                )}
              </>
            )
          ) : (
            <>
              <InsightsOutlined
                sx={{
                  fontSize: 24,
                  color: theme.palette.primary.main,
                }}
              />
              <Typography variant="subtitle2" fontWeight={400}>
                Estimated Impact
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  textAlign: 'center',
                  px: 1,
                }}
              >
                Enter Trigger Group, Event and Trigger Source(s) to enable
                simulation.
              </Typography>
            </>
          )}
        </Stack>
      </Box>
    </Card>
  );
}
